<template>

<div v-if="rows.length" class="guesses-wrap">

	<transition-group name="pop" tag="ul" class="guesses">

		<li
			v-for="row in visibleRows"
			:key="row.id + '-' + row.n"
			class="guess-row"
			:class="row.cls"
		>
			<span class="guess-row-lead">
				<span class="guess-row-index">Guess {{ row.n }}</span>
				<span class="guess-row-name">{{ row.name }}</span>
			</span>
			<span class="guess-row-mark">
				<span class="guess-row-dot"></span>
				<span class="guess-row-label">{{ row.mark }}</span>
			</span>
		</li>

	</transition-group>

	<button
		v-if="rows.length > LIMIT"
		type="button"
		class="guesses-toggle"
		v-on:click="showAll = !showAll"
	>{{ showAll ? 'Show fewer' : 'View all ' + rows.length + ' guesses' }}</button>

</div>

</template>

<script>

/*
	The list of this puzzle's guesses, NEWEST FIRST, numbered ("Guess 4"). Only
	the most recent few are shown by default with a "View all" toggle for the
	rest. A correct pick is green ("Got it"), a wrong one terracotta ("Not it"),
	and a skip is muted ("Skipped"). New rows pop in via a transition-group.

	game.js stores guessed game IDS only (0 = a skip), so the host screen passes
	a `names` map (id -> display name from the chosen suggestion). The winning
	row is the pick matching game.result.answer once solved.
*/

var LIMIT = 5

export default {

	props: {
		// id -> name, supplied by the Daily screen as picks are committed
		names: {
			type: Object,
			default: function() {

				return {}

			}
		}
	},

	data: function() {

		return {
			LIMIT: LIMIT,
			showAll: false
		}

	},

	computed: {

		picks: function() {

			return this.$store.state.game.picks

		},

		result: function() {

			return this.$store.getters['game/result']

		},

		// a guess is in flight; its row shows neutral "Checking…" rather than
		// flashing wrong before the server confirms (esp. a winning guess)
		loading: function() {

			return this.$store.state.game.loading

		},

		// the answer's game id, known only once the puzzle is solved
		answerId: function() {

			var answer = this.result && this.result.answer

			if (!answer) return null

			return answer.id != null ? answer.id : answer

		},

		// picks turned into display rows, numbered in play order, then reversed
		// so the most recent guess sits at the top
		rows: function() {

			var self = this

			var last = this.picks.length - 1

			return this.picks.map(function(id, index) {

				var skip = id <= 0
				var correct = self.isCorrect(id)
				// the newest pick, while its request is in flight, is undecided
				var pending = self.loading && index === last && !self.result

				return {
					id: id,
					n: index + 1,
					name: skip ? 'Skipped' : self.nameFor(id),
					mark: pending ? 'Checking…' : (correct ? 'Got it' : (skip ? 'Skipped' : 'Not it')),
					cls: pending ? 'is-pending' : (correct ? 'is-correct' : (skip ? 'is-skip' : 'is-wrong'))
				}

			}).reverse()

		},

		visibleRows: function() {

			return this.showAll ? this.rows : this.rows.slice(0, LIMIT)

		}

	},

	methods: {

		isCorrect: function(id) {

			return this.result && this.result.solved && this.answerId === id

		},

		nameFor: function(id) {

			if (this.names && this.names[id]) return this.names[id]

			// a solved row whose name wasn't cached can borrow the answer's name
			if (this.isCorrect(id) && this.result.answer && this.result.answer.name) {

				return this.result.answer.name

			}

			return 'Game #' + id

		}

	}

}

</script>

<style scoped>

.guesses-wrap {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.guesses {
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.guess-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 11px 13px;
	border-radius: 7px;
	border: 1px solid;
}

.guess-row.is-wrong {
	background: var(--wrong-tint);
	border-color: #f0e3df;
}

.guess-row.is-correct {
	background: var(--correct-tint);
	border-color: #cfe0c8;
}

.guess-row.is-skip {
	background: transparent;
	border-color: var(--hairline);
}

.guess-row.is-pending {
	background: #f6f2ea;
	border-color: var(--hairline);
}

.guess-row.is-pending .guess-row-dot {
	background: var(--muted);
	animation: pending-blink 900ms ease-in-out infinite;
}

.guess-row.is-pending .guess-row-name,
.guess-row.is-pending .guess-row-label {
	color: var(--muted);
}

@keyframes pending-blink {
	0%, 100% { opacity: 0.3; }
	50% { opacity: 1; }
}

.guess-row-lead {
	display: flex;
	align-items: baseline;
	gap: 10px;
	min-width: 0;
}

.guess-row-index {
	font-family: var(--font-mono);
	font-size: 10px;
	letter-spacing: 0.12em;
	text-transform: uppercase;
	color: var(--muted);
	flex: none;
}

.guess-row-name {
	font-family: var(--font-body);
	font-size: 14px;
	color: var(--ink);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.guess-row.is-skip .guess-row-name {
	color: var(--muted);
}

.guess-row-mark {
	display: flex;
	align-items: center;
	gap: 7px;
	flex: none;
}

.guess-row-dot {
	width: 6px;
	height: 6px;
	border-radius: 50%;
}

.guess-row.is-wrong .guess-row-dot {
	background: var(--wrong);
}

.guess-row.is-correct .guess-row-dot {
	background: var(--correct);
}

.guess-row.is-skip .guess-row-dot {
	background: var(--muted);
}

.guess-row-label {
	font-family: var(--font-mono);
	font-size: 10px;
	letter-spacing: 0.1em;
	text-transform: uppercase;
}

.guess-row.is-wrong .guess-row-label {
	color: var(--wrong);
}

.guess-row.is-correct .guess-row-label {
	color: var(--correct);
}

.guess-row.is-skip .guess-row-label {
	color: var(--muted);
}

.guesses-toggle {
	align-self: center;
	padding: 6px 12px;
	font-family: var(--font-mono);
	font-size: 10px;
	letter-spacing: 0.12em;
	text-transform: uppercase;
	color: var(--accent);
	background: transparent;
	border: none;
	cursor: pointer;
}

.guesses-toggle:hover {
	text-decoration: underline;
}

/* new guesses pop into the list; the rest slide down to make room */
.pop-enter-active {
	animation: guess-pop 380ms cubic-bezier(0.34, 1.56, 0.64, 1);
}

.pop-move {
	transition: transform 300ms ease;
}

@keyframes guess-pop {
	0% {
		opacity: 0;
		transform: scale(0.85) translateY(-6px);
	}
	60% {
		opacity: 1;
		transform: scale(1.04) translateY(0);
	}
	100% {
		transform: scale(1) translateY(0);
	}
}

@media (prefers-reduced-motion: reduce) {

	.pop-enter-active {
		animation: none;
	}

	.pop-move {
		transition: none;
	}

}

</style>
